<script setup lang="ts">
import { Play } from 'lucide-vue-next'
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
import type { CSSProperties } from 'vue'

import { FLASHCARD_INTERACTIVE } from '../../flashcard/context'

import placeholderPosterSrc from '../../assets/placeholders/video.svg'

const props = defineProps<{
  src: string
  poster?: string
  autoplay: boolean
  loop: boolean
  muted: boolean
  playsInline: boolean
  preload: 'none' | 'metadata' | 'auto'
  style?: CSSProperties
  iconSizePx?: number
}>()

const isInteractive = inject(
  FLASHCARD_INTERACTIVE,
  computed(() => true),
)
const isCanvas = computed(() => isInteractive.value === false)

const videoRef = ref<HTMLVideoElement | null>(null)
const isPlaying = ref(false)

const getYouTubeId = (value: string) => {
  // Supports: https://youtu.be/<id>, https://www.youtube.com/watch?v=<id>, /embed/<id>, /shorts/<id>
  try {
    const url = new URL(value)
    const host = url.hostname.replace(/^www\./, '')

    if (host === 'youtu.be') {
      const id = url.pathname.replace(/^\//, '').split('/')[0]
      return id || null
    }

    if (host !== 'youtube.com' && host !== 'm.youtube.com' && host !== 'music.youtube.com') {
      return null
    }

    const watchId = url.searchParams.get('v')
    if (watchId) return watchId

    const parts = url.pathname.split('/').filter(Boolean)
    const embedIndex = parts.indexOf('embed')
    if (embedIndex >= 0) return parts[embedIndex + 1] ?? null

    const shortsIndex = parts.indexOf('shorts')
    if (shortsIndex >= 0) return parts[shortsIndex + 1] ?? null

    return null
  } catch {
    return null
  }
}

const youTubeId = computed(() => getYouTubeId(props.src))
const isYouTube = computed(() => Boolean(youTubeId.value))

const youTubePoster = computed(() => {
  if (!youTubeId.value) return undefined
  return `https://img.youtube.com/vi/${youTubeId.value}/hqdefault.jpg`
})

type VimeoInfo = { id: string; hash: string }

const getVimeoInfo = (value: string): VimeoInfo | null => {
  // Supports: https://vimeo.com/<id>, https://vimeo.com/<id>/<hash> (unlisted/private videos),
  // https://vimeo.com/channels/<name>/<id>, https://vimeo.com/groups/<name>/videos/<id>,
  // https://player.vimeo.com/video/<id>?h=<hash>
  try {
    const url = new URL(value)
    const host = url.hostname.replace(/^www\./, '')
    if (host !== 'vimeo.com' && host !== 'player.vimeo.com') return null

    const segments = url.pathname.split('/').filter(Boolean)
    let id = ''
    let hash = ''

    if (host === 'player.vimeo.com') {
      const idx = segments.indexOf('video')
      id = (idx >= 0 ? segments[idx + 1] : segments[0]) ?? ''
      hash = (idx >= 0 ? segments[idx + 2] : segments[1]) ?? ''
    } else {
      const idPos = segments.findIndex((segment) => /^\d+$/.test(segment))
      id = (idPos >= 0 ? segments[idPos] : '') ?? ''
      const next = idPos >= 0 ? segments[idPos + 1] : undefined
      if (next && /^[A-Za-z0-9]+$/.test(next)) hash = next
    }

    if (!hash) hash = url.searchParams.get('h') ?? ''
    if (!/^\d+$/.test(id)) return null

    return { id, hash }
  } catch {
    return null
  }
}

const vimeoInfo = computed(() => getVimeoInfo(props.src))
const isVimeo = computed(() => Boolean(vimeoInfo.value))

const vimeoEmbedUrl = computed(() => {
  if (!vimeoInfo.value) return undefined

  const { id, hash } = vimeoInfo.value
  const params = new URLSearchParams({
    autoplay: '1',
    controls: '0',
    dnt: '1',
    title: '0',
    byline: '0',
    portrait: '0',
    playsinline: props.playsInline ? '1' : '0',
  })

  if (hash) params.set('h', hash)
  if (props.muted) params.set('muted', '1')
  if (props.loop) params.set('loop', '1')

  return `https://player.vimeo.com/video/${id}?${params.toString()}`
})

const isEmbedProvider = computed(() => isYouTube.value || isVimeo.value)

const effectivePoster = computed(() => {
  if (props.poster) return props.poster
  return youTubePoster.value
})

const fallbackPoster = computed(() => {
  const src = typeof props.src === 'string' ? props.src.trim() : ''
  if (!src) return placeholderPosterSrc
  return undefined
})

const youTubeEmbedUrl = computed(() => {
  if (!youTubeId.value) return undefined

  const params = new URLSearchParams({
    autoplay: '1',
    controls: '0',
    modestbranding: '1',
    rel: '0',
    fs: '0',
    iv_load_policy: '3',
    disablekb: '1',
    cc_load_policy: '0',
    playsinline: props.playsInline ? '1' : '0',
  })

  if (props.muted) {
    params.set('mute', '1')
  }

  if (props.loop) {
    params.set('loop', '1')
    // YouTube requires playlist for looping a single video.
    params.set('playlist', youTubeId.value)
  }

  return `https://www.youtube-nocookie.com/embed/${youTubeId.value}?${params.toString()}`
})

const updatePlayingState = () => {
  const el = videoRef.value
  if (!el) {
    isPlaying.value = false
    return
  }
  isPlaying.value = !el.paused && !el.ended
}

const stopPlayback = () => {
  const el = videoRef.value
  if (!el) return

  el.pause()
  try {
    el.currentTime = 0
  } catch {
    // Streams may not be seekable.
  }
  updatePlayingState()
}

const startPlayback = async () => {
  const el = videoRef.value
  if (!el) return

  try {
    await el.play()
  } catch {
    // Autoplay policies / missing media.
  }
  updatePlayingState()
}

const handlePointerDown = (event: PointerEvent) => {
  if (isCanvas.value) return
  event.preventDefault()
  event.stopPropagation()
}

const togglePlayback = async (event: MouseEvent) => {
  if (isCanvas.value) return

  event.preventDefault()
  event.stopPropagation()

  if (isEmbedProvider.value) {
    isPlaying.value = !isPlaying.value
    return
  }

  if (isPlaying.value) {
    stopPlayback()
    return
  }

  await startPlayback()
}

const wrapperStyle = computed<CSSProperties>(() => ({
  ...(props.style ?? {}),
  position: 'absolute',
  display: 'block',
  overflow: 'hidden',
  background: 'transparent',
}))

const iconStyle = computed<CSSProperties>(() => {
  const size = typeof props.iconSizePx === 'number' && props.iconSizePx > 0 ? props.iconSizePx : 26
  return { width: `${size}px`, height: `${size}px` }
})

onMounted(() => {
  const el = videoRef.value
  if (!el) return
  el.addEventListener('play', updatePlayingState)
  el.addEventListener('pause', updatePlayingState)
  el.addEventListener('ended', updatePlayingState)
  updatePlayingState()
})

onBeforeUnmount(() => {
  const el = videoRef.value
  if (!el) return
  el.removeEventListener('play', updatePlayingState)
  el.removeEventListener('pause', updatePlayingState)
  el.removeEventListener('ended', updatePlayingState)
})
</script>

<template>
  <div
    class="video-shell"
    :style="wrapperStyle"
    :data-playing="isPlaying ? 'true' : 'false'"
    @pointerdown.capture="handlePointerDown"
    @click.capture="togglePlayback"
  >
    <template v-if="isYouTube">
      <img
        v-if="!isPlaying"
        class="video-poster"
        :src="effectivePoster"
        alt=""
        aria-hidden="true"
      />

      <iframe
        v-else
        class="video-iframe"
        :src="youTubeEmbedUrl"
        title="YouTube video"
        frameborder="0"
        allow="autoplay; encrypted-media; picture-in-picture"
      />
    </template>

    <template v-else-if="isVimeo">
      <img
        v-if="!isPlaying"
        class="video-poster"
        :src="effectivePoster || fallbackPoster"
        alt=""
        aria-hidden="true"
      />

      <iframe
        v-else
        class="video-iframe"
        :src="vimeoEmbedUrl"
        title="Vimeo video"
        frameborder="0"
        allow="autoplay; fullscreen; picture-in-picture"
      />
    </template>

    <video
      v-else
      ref="videoRef"
      class="video-media"
      :src="props.src"
      :poster="effectivePoster || fallbackPoster || undefined"
      :autoplay="props.autoplay"
      :loop="props.loop"
      :muted="props.muted"
      :preload="props.preload"
      :playsinline="props.playsInline"
    />

    <div class="video-overlay" aria-hidden="true">
      <span class="video-play">
        <Play class="video-play-icon" :style="iconStyle" />
      </span>
    </div>
  </div>
</template>

<style scoped>
.video-poster {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  pointer-events: none;
}

.video-iframe {
  width: 100%;
  height: 100%;
  display: block;
  border: 0;
  pointer-events: none;
}

.video-media {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  pointer-events: none;
  background: transparent;
}

.video-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  opacity: 1;
}
.video-play {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: transparent;
  /* Shadow-only circle behind the icon (no fill). */
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.45);
}

.video-play-icon {
  color: rgba(248, 250, 252, 0.95);
  transform: translateX(1px);
  filter: drop-shadow(0 4px 14px rgba(0, 0, 0, 0.7));
}

.video-shell[data-playing='true'] .video-overlay {
  opacity: 0;
}
</style>
